import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit'
import { authApi } from '../api/modules/authApi.js'
import { getRoleLabel, isAdminPortalRole } from '../constants/roles.js'
import { authStorage } from '../utils/authStorage.js'

const getSessionFromPayload = (payload) => payload?.data ?? payload

const getAuthErrorMessage = (error, fallback) => (
  error.response?.data?.message
  || error.message
  || fallback
)

const finalizeAdminSession = (payload, rejectWithValue) => {
  const session = getSessionFromPayload(payload)
  const role = session?.user?.role

  if (!isAdminPortalRole(role)) {
        authStorage.clearAll()
        return rejectWithValue(
          `This portal is only for admin accounts. ${getRoleLabel(role)} users should continue in the BrainCamp mobile app.`
        )
      }

  authStorage.persistSession(session)
  return session
}

export const loginThunk = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const payload = await authApi.login({ email, password })
      return finalizeAdminSession(payload, rejectWithValue)
    } catch (err) {
      return rejectWithValue(getAuthErrorMessage(err, 'Login failed. Please check credentials.'))
    }
  }
)

export const loginWithOtpThunk = createAsyncThunk(
  'auth/loginWithOtp',
  async ({ mobile, otp }, { rejectWithValue }) => {
    try {
      const payload = await authApi.verifyOtp({ mobile, otp })
      return finalizeAdminSession(payload, rejectWithValue)
    } catch (err) {
      return rejectWithValue(getAuthErrorMessage(err, 'OTP verification failed. Please try again.'))
    }
  }
)

export const googleLoginThunk = createAsyncThunk(
  'auth/googleLogin',
  async ({ googleToken }, { rejectWithValue }) => {
    try {
      const payload = await authApi.googleLogin({ googleToken })
      return finalizeAdminSession(payload, rejectWithValue)
    } catch (err) {
      return rejectWithValue(getAuthErrorMessage(err, 'Google sign-in failed. Please try again.'))
    }
  }
)

export const logoutThunk = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  try { await authApi.logout() } catch {}
  authStorage.clearAll()
  dispatch(clearAuth())
})

export const restoreSession = createAsyncThunk(
  'auth/restoreSession',
  async (_, { rejectWithValue }) => {
    const session = authStorage.loadSession()

    if (!session.isAuthenticated) {
      return rejectWithValue('No active session')
    }

    if (!isAdminPortalRole(session.user?.role)) {
      authStorage.clearAll()
      return rejectWithValue('Admin portal access is restricted to admin accounts')
    }

    return session
  }
)

const initialState = {
  ...authStorage.loadSession(),
  isNewLogin: false,
  loading: false,
  error: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuth(state) {
      state.user = null
      state.token = null
      state.schoolConfig = null
      state.isAuthenticated = false
      state.isNewLogin = false
      state.error = null
    },
    restoreSessionState(state) {
      const saved = authStorage.loadSession()
      state.user = saved.user
      state.token = saved.token
      state.schoolConfig = saved.schoolConfig
      state.isAuthenticated = saved.isAuthenticated
    },
    clearError(state) {
      state.error = null
    },
    clearNewLogin(state) {
      state.isNewLogin = false
    },
    setSchoolConfig(state, action) {
      state.schoolConfig = action.payload
      if (action.payload) {
        authStorage.saveSchoolConfig(action.payload)
      }
    },
    updateUser(state, action) {
      if (!state.user) return
      state.user = { ...state.user, ...action.payload }
      authStorage.saveUser(state.user)
    },
    forceLogout(state) {
      state.user = null
      state.token = null
      state.schoolConfig = null
      state.isAuthenticated = false
      state.isNewLogin = false
      state.error = null
      authStorage.clearAll()
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(restoreSession.pending, (state) => {
        state.loading = true
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.token = action.payload.token
        state.schoolConfig = action.payload.schoolConfig
        state.isAuthenticated = true
      })
      .addCase(restoreSession.rejected, (state) => {
        state.loading = false
        state.user = null
        state.token = null
        state.schoolConfig = null
        state.isAuthenticated = false
      })
      .addCase(logoutThunk.pending, (state) => {
        state.loading = true
      })
      .addCase(logoutThunk.fulfilled, (state) => {
        state.loading = false
        state.user = null
        state.token = null
        state.schoolConfig = null
        state.isAuthenticated = false
        state.isNewLogin = false
      })
      .addMatcher(isAnyOf(
        loginThunk.pending,
        loginWithOtpThunk.pending,
        googleLoginThunk.pending,
      ), (state) => {
        state.loading = true
        state.error = null
      })
      .addMatcher(isAnyOf(
        loginThunk.fulfilled,
        loginWithOtpThunk.fulfilled,
        googleLoginThunk.fulfilled,
      ), (state, action) => {
        state.loading = false
        const { token, user, schoolConfig } = action.payload
        state.token = token || null
        state.user = user
        state.schoolConfig = schoolConfig || null
        state.isAuthenticated = !!user
        state.isNewLogin = true
      })
      .addMatcher(isAnyOf(
        loginThunk.rejected,
        loginWithOtpThunk.rejected,
        googleLoginThunk.rejected,
      ), (state, action) => {
        state.loading = false
        state.user = null
        state.token = null
        state.schoolConfig = null
        state.isAuthenticated = false
        state.isNewLogin = false
        state.error = action.payload
      })
  },
})

export const {
  clearAuth,
  restoreSessionState,
  clearError,
  clearNewLogin,
  setSchoolConfig,
  updateUser,
  forceLogout,
} = authSlice.actions
export default authSlice.reducer
